import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useProducts } from '../../hooks/use-products';
import {
  useAssociationGroups,
  useCreateAssociationGroup,
  useAddProductsToGroup,
  useUpdateAssociationGroup,
  useDeleteAssociationGroup,
} from '../../hooks/use-associations';
import { useAssociationState } from './use-association-state';
import { MAX_PRODUCTS } from './constants';
import type { AssociationGroup, Product, CreateProductGroupItemDto } from '@/lib/types';
import type { AssociationManagerActions } from './types';

interface UseAssociationManagerOptions {
  currentProduct: Product;
  shopId: string;
  onClose: () => void;
}

export function useAssociationManager({ currentProduct, shopId, onClose }: UseAssociationManagerOptions) {
  const { t } = useTranslation();
  const { state, setters, resetState, resetGroupForm } = useAssociationState();

  const { data: groups, isLoading: isLoadingGroups } = useAssociationGroups();
  const { data: productsData, isLoading: isLoadingProducts } = useProducts({
    shopId,
    limit: 50,
    search: state.searchQuery || undefined,
  });

  const products = productsData?.products ?? [];

  const createGroup = useCreateAssociationGroup();
  const addProducts = useAddProductsToGroup();
  const updateGroup = useUpdateAssociationGroup();
  const deleteGroup = useDeleteAssociationGroup();

  const currentProductId = currentProduct.refId || currentProduct.id;

  const availableProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((p: Product) => (p.refId || p.id) !== currentProductId);
  }, [products, currentProductId]);

  const handleClose = () => { resetState(); onClose(); };

  const handleSelectGroup = (group: AssociationGroup) => {
    setters.setSelectedGroup(group);
    setters.setStep('select_products');
  };

  const handleDeleteGroup = async (group: AssociationGroup) => {
    try {
      await deleteGroup.mutateAsync(group.groupId);
      toast.success(t('associations.groupDeleted'));
      setters.setGroupToDelete(null);
    } catch { toast.error(t('common.error')); }
  };

  const handleEditGroup = (group: AssociationGroup) => {
    setters.setGroupToEdit(group);
    setters.setEditGroupName(group.name);
    setters.setEditGroupDescription(group.description || '');
    setters.setStep('edit_group');
  };

  const handleSaveEditGroup = async () => {
    if (!state.groupToEdit || !state.editGroupName.trim()) {
      toast.error(t('associations.groupNameRequired')); return;
    }
    try {
      await updateGroup.mutateAsync({
        groupId: state.groupToEdit.groupId,
        name: state.editGroupName,
        description: state.editGroupDescription,
      });
      toast.success(t('associations.groupUpdated'));
      setters.setGroupToEdit(null);
      setters.setStep('select_group');
    } catch { toast.error(t('common.error')); }
  };

  const handleCreateNewGroup = () => setters.setStep('create_group');
  const handleBackToGroups = () => { setters.setStep('select_group'); resetGroupForm(); };
  const handleBackToProducts = () => { setters.setStep('select_products'); setters.setVariantConfigs({}); };

  const handleCreateGroup = async () => {
    if (!state.newGroupName.trim()) { toast.error(t('associations.groupNameRequired')); return; }
    try {
      const newGroup = await createGroup.mutateAsync({
        name: state.newGroupName, shopId,
        description: state.newGroupDescription || state.newGroupName,
        variationType: state.newGroupVariationType,
      });
      setters.setSelectedGroup(newGroup);
      setters.setStep('select_products');
      toast.success(t('associations.groupCreated'));
    } catch { toast.error(t('common.error')); }
  };

  const handleToggleProduct = (productId: string) => {
    setters.setSelectedProductIds((prev) => {
      if (prev.includes(productId)) return prev.filter((id) => id !== productId);
      if (prev.length >= MAX_PRODUCTS) { toast.error(t('associations.maxProductsReached', { max: MAX_PRODUCTS })); return prev; }
      return [...prev, productId];
    });
  };

  const handleSubmit = async () => {
    if (!state.selectedGroup) return;
    try {
      if (state.selectedGroup.variationType === 'related') {
        await addProducts.mutateAsync({ productRefIds: [currentProductId, ...state.selectedProductIds], groupId: state.selectedGroup.groupId, shopId });
      } else {
        await updateGroup.mutateAsync({ groupId: state.selectedGroup.groupId, items: Object.values(state.variantConfigs) });
      }
      toast.success(t('associations.productsAssociated'));
      handleClose();
    } catch { toast.error(t('common.error')); }
  };

  const handleContinueToVariants = () => {
    if (state.selectedProductIds.length === 0) { toast.error(t('associations.selectAtLeastOne')); return; }
    const variationType = state.selectedGroup?.variationType || state.newGroupVariationType;
    if (variationType === 'related') { handleSubmit(); return; }
    const initialConfigs: Record<string, CreateProductGroupItemDto> = {};
    [currentProductId, ...state.selectedProductIds].forEach((id, i) => {
      initialConfigs[id] = { productRefId: id, displayOrder: i, isDefault: i === 0 };
    });
    setters.setVariantConfigs(initialConfigs);
    setters.setStep('configure_variants');
  };

  const handleUpdateVariantConfig = (productId: string, field: keyof CreateProductGroupItemDto, value: string | number | boolean) => {
    setters.setVariantConfigs((prev) => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));
  };

  const actions: AssociationManagerActions = {
    ...setters,
    handleClose,
    handleSelectGroup,
    handleDeleteGroup,
    handleEditGroup,
    handleSaveEditGroup,
    handleCreateNewGroup,
    handleBackToGroups,
    handleBackToProducts,
    handleCreateGroup,
    handleToggleProduct,
    handleContinueToVariants,
    handleUpdateVariantConfig,
    handleSubmit,
  };

  return {
    state,
    actions,
    data: { groups, availableProducts, currentProductId },
    loading: { isLoadingGroups, isLoadingProducts },
    mutations: { createGroup, addProducts, updateGroup, deleteGroup },
  };
}
